import React, { useEffect, useState, useRef } from 'react';
import { v4 as uuid } from 'uuid';
import { useSelector } from 'react-redux';

// Api
import {
  createNewCustomer,
  updateCustomer,
  addPaymentMethod,
  deletePaymentMethod,
} from 'utils/api/customerApi';
import {
  runCNPZeroDollarAuth,
  createDraftTransaction,
  runTerminalZeroDollarAuth,
  cancelTerminalSale,
  deleteCardTokens,
} from 'utils/api/globalpayApi';
import GlobalPayPayFieldsForm from 'components/Layout/Content/Payment/GlobalPayPayFieldsForm';
import DeleteButton from 'components/Layout/Content/Customers/DeleteButton';
import CardRadioButton from 'components/Common/CardRadio/CardRadioButton';

// Utils
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { parseNames, getFirstName, getLastName } from 'utils/helpers/names';

// Components
import {
  Modal,
  ModalTitle,
  ModalBody,
  ModalFooter,
  Button,
  Spinner,
} from 'react-bootstrap';
import ModalHeader from 'react-bootstrap/ModalHeader';
import CustomerInfo from './CustomerInfo';

// Styles
import styles from './CustomerModal.module.scss';

const CUSTOMER_INFO_STEP = 0;
const CARD_INFO_STEP = 1;
const ADD_CARD_STEP = 2;
const SELECT_TERMINAL_STEP = 3;
const WAIT_TERMINAL_STEP = 4;

const editModes = Object.freeze({
  NEW: 0,
  INFO: 1,
  CARD: 2,
});

const ERROR_SAVING_CUSTOMER =
  'There was a problem saving the customer, please try again';
const ERROR_ADDING_CARD =
  'There was a problem adding the card, please try again';

function GlobalPayCustomerModal(props) {
  const {
    show,
    onHide,
    onAdd,
    onUpdate,
    editMode: currentEditMode = editModes.NEW,
    customerToEdit = null,
    practiceId,
  } = props;

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
    getValues,
  } = useForm();

  const [isLoading, setLoading] = useState(false);
  const [modalStep, setModalStep] = useState(CUSTOMER_INFO_STEP);
  const [isGlobalPayLoading, setIsGlobalPayLoading] = useState(false);
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const [draftTransactionId, setDraftTransactionId] = useState('');
  const [deletedPaymentMethodIds, setDeletedPaymentMethodIds] = useState([]);
  const [newCustomerId, setNewCustomerId] = useState();
  const newCustomerPaymentMethod = useRef();

  const globalPayDeviceIds = useSelector(
    (s) => s.loggedPractice.globalPayDeviceIds
  );

  useEffect(() => {
    if (selectedDeviceId === '' && globalPayDeviceIds) {
      if (globalPayDeviceIds.length > 0)
        setSelectedDeviceId(globalPayDeviceIds[0][0]);
    }
  }, [globalPayDeviceIds, selectedDeviceId]);

  useEffect(() => {
    if (show) {
      switch (currentEditMode) {
        case editModes.NEW:
        case editModes.INFO:
          setModalStep(CUSTOMER_INFO_STEP);
          break;
        case editModes.CARD:
          setModalStep(CARD_INFO_STEP);
          break;
        default:
          throw new Error(`Unrecognized edit mode ${currentEditMode}`);
      }
    }
  }, [show, currentEditMode, customerToEdit, practiceId]);

  async function handleModalExited() {
    reset();
    setLoading(false);
    setIsGlobalPayLoading(false);
    setModalStep(CUSTOMER_INFO_STEP);
  }

  async function handleBackwardClick() {
    if (modalStep === ADD_CARD_STEP || modalStep === SELECT_TERMINAL_STEP) {
      setModalStep(CARD_INFO_STEP);
    } else if (modalStep === WAIT_TERMINAL_STEP) {
      setModalStep(SELECT_TERMINAL_STEP);
    } else if (
      modalStep === CUSTOMER_INFO_STEP ||
      currentEditMode === editModes.CARD
    ) {
      onHide();
    } else {
      setModalStep(CUSTOMER_INFO_STEP);
    }
  }

  function handleForwardClick(e) {
    // this function is triggered whenever clicking a button, so we want to only actually run it if it's when updating info, the rest is called directly
    e.preventDefault(); // prevent form from submitting initially

    if (
      (currentEditMode === editModes.INFO ||
        currentEditMode === editModes.NEW) &&
      modalStep === CUSTOMER_INFO_STEP
    )
      handleSubmit(onSubmit)(); // use handleSubmit for field validation
  }

  function handleAddCardByCNP() {
    setIsGlobalPayLoading(true);
    setModalStep(ADD_CARD_STEP);
  }

  async function handleAddCardByTerminal() {
    setLoading(true);

    // we have to use a draft transaction here just so that we can have a stored reference id to be able to cancel the terminal sale
    const draftTransactionInfo = {
      invoiceId: 'ZDA-inv', // this is just for the draft transaction, isn't sent to GlobalPay
      amount: 0,
      globalPayDeviceId: selectedDeviceId,
    };

    if (currentEditMode === editModes.NEW) {
      draftTransactionInfo.petOwnerName = getValues('customerData.full_name');
      draftTransactionInfo.petOwnerPhone = getValues('customerData.phone');
    } else {
      draftTransactionInfo.petOwnerName = `${customerToEdit.first_name} ${customerToEdit.last_name}`;
      draftTransactionInfo.petOwnerPhone = customerToEdit.phone;
    }

    try {
      const draft = await createDraftTransaction(draftTransactionInfo);

      setDraftTransactionId(draft.transactionId);

      setModalStep(WAIT_TERMINAL_STEP);

      handleContinueTerminalAuth(draft.transactionId);
    } catch (error) {
      console.error(error);
      if (error.errorMessage) {
        toast.error(error.errorMessage);
      }
    }

    setLoading(false);
  }

  async function handleContinueTerminalAuth(draftTransId) {
    try {
      const zdaAuthResponse = await runTerminalZeroDollarAuth(
        selectedDeviceId,
        draftTransId
      );

      await handleAfterPaymentSuccess(zdaAuthResponse);
    } catch (error) {
      console.error(error);
      if (error.errorMessage) {
        toast.error(error.errorMessage);
      }
      handleBackwardClick();
    }

    setLoading(false);
  }

  async function handlePayFieldInformationAccepted(temp_token) {
    setLoading(true);

    try {
      const address = {};
      const zdaAuthResponse = await runCNPZeroDollarAuth(temp_token, address);

      await handleAfterPaymentSuccess(zdaAuthResponse);
    } catch (error) {
      console.error(error);
      if (error.errorMessage) {
        toast.error(error.errorMessage);
      }
    }

    setLoading(false);
  }

  async function handleAfterPaymentSuccess(zdaAuthResponse) {
    const paymentMethod = {
      last_four: zdaAuthResponse.last_four,
      payment_method_token: zdaAuthResponse.gpToken,
      full_name: zdaAuthResponse.holderFullName,
      card_type: zdaAuthResponse.brand,
      expiration_month: zdaAuthResponse.exp_month,
      expiration_year: zdaAuthResponse.exp_year,
    };
    if (currentEditMode === editModes.NEW) {
      newCustomerPaymentMethod.current = paymentMethod;

      handleSubmit(onSubmit)();
    } else {
      const newPaymentMethod = await addPaymentMethod(
        paymentMethod,
        customerToEdit?.customer_id
      );
      console.log(newPaymentMethod);

      onUpdate({
        customer_id: customerToEdit.customer_id,
        payment_methods: [{ ...newPaymentMethod.data }],
      });
      onHide();
    }
  }

  async function handleCancelTerminalAuth() {
    try {
      const cancelResult = await cancelTerminalSale(draftTransactionId);
      console.log(cancelResult);

      setModalStep(CARD_INFO_STEP);
    } catch (error) {
      console.error('Error when trying to cancel terminal sale: ', error);
      if (error.errorMessage) {
        toast.error(
          'Error when trying to cancel terminal sale: ' + error.errorMessage
        );
      }
    }
  }

  async function handleDeleteCard(pmId) {
    setLoading(true);

    const res = await deletePaymentMethod(customerToEdit.customer_id, pmId);

    console.log(res);

    try {
      const gpRes = await deleteCardTokens(
        customerToEdit.payment_methods
          .filter((pm) => pm.id === pmId)
          .map((pm) => pm.payment_method_token)
      );

      console.log(gpRes);

      setDeletedPaymentMethodIds([...deletedPaymentMethodIds, pmId]);
    } catch (error) {
      console.error(error);
      if (error.errorMessage) {
        toast.error(error.errorMessage);
      }
    }

    setLoading(false);
  }

  async function onSubmit(formData) {
    if (modalStep === CUSTOMER_INFO_STEP) {
      const [firstName, lastName] = parseNames(formData.customerData.full_name);
      if (currentEditMode === editModes.INFO && customerToEdit) {
        setLoading(true);

        try {
          const updateRecord = {
            customerData: formData.customerData,
            addressData: formData.addressData,
          };
          updateRecord.addressData.id = customerToEdit.addresses[0]?.id;

          updateRecord.customerData.first_name = firstName;
          updateRecord.customerData.last_name = lastName;

          const updatedCustomer = await updateCustomer(
            updateRecord,
            customerToEdit.customer_id
          );

          onUpdate(updatedCustomer.data);
          onHide();
        } catch (e) {
          console.error(e);

          setLoading(false);
          toast.error(ERROR_SAVING_CUSTOMER);
        }
      } else {
        setModalStep(CARD_INFO_STEP);
        const randomCustomerId = uuid();
        setNewCustomerId(randomCustomerId);
      }

      return;
    }

    if (
      currentEditMode === editModes.NEW &&
      (modalStep === ADD_CARD_STEP ||
        modalStep === WAIT_TERMINAL_STEP ||
        modalStep === SELECT_TERMINAL_STEP) // the state hasnt updated so it might still be select terminal step even if it should be wait terminal step
    ) {
      try {
        setLoading(true);

        const paymentData = newCustomerPaymentMethod.current;
        const fullName = formData.customerData.full_name;
        const insertRecord = {
          customerData: {
            customer_id: newCustomerId,
            first_name: getFirstName(fullName),
            last_name: getLastName(fullName),
            phone: formData.customerData.phone,
          },
          addressData: {
            address_1: formData.addressData.address_1,
            address_2: formData.addressData.address_2,
            city: formData.addressData.city,
            state: formData.addressData.state,
            zip: formData.addressData.zip,
          },
          paymentMethodData: paymentData,
        };
        const newCustomer = await createNewCustomer(insertRecord);
        onAdd(newCustomer.data);

        onHide();
      } catch (e) {
        console.error(e);

        setLoading(false);
        toast.error(ERROR_ADDING_CARD);
      }
    }
  }

  return (
    <Modal
      show={show}
      onHide={onHide}
      onExited={handleModalExited}
      dialogClassName={`${styles.modalBody}`}
    >
      <ModalHeader>
        <ModalTitle>
          {currentEditMode === editModes.NEW && 'Add New Customer'}
          {currentEditMode === editModes.INFO && 'Edit Customer'}
          {currentEditMode === editModes.CARD && 'Add Saved Card'}
        </ModalTitle>
      </ModalHeader>
      <form onSubmit={handleForwardClick}>
        <ModalBody className={`${styles.modalBody}`}>
          {isLoading && (
            <div className={styles.loadingSpinner}>
              <Spinner animation='border' variant='secondary' />
            </div>
          )}
          {!isLoading && modalStep === CUSTOMER_INFO_STEP && (
            <CustomerInfo
              register={register}
              errors={errors}
              control={control}
              customerToEdit={customerToEdit}
              isGlobalPay={true}
            />
          )}
          {!isLoading && modalStep === CARD_INFO_STEP && (
            <div className={styles.gpCardInfoStep}>
              {currentEditMode !== editModes.NEW && (
                <div className={styles.gpCardTable}>
                  {customerToEdit?.payment_methods
                    .filter((pm) => !deletedPaymentMethodIds.includes(pm.id))
                    .map((paymentMethod) => (
                      <div className={styles.gpCardContainer}>
                        <div className={styles.gpCardContainerSubCell}>
                          <div className={styles.gpCardName}>
                            {paymentMethod.full_name ?? 'Missing Name'}
                          </div>
                          <DeleteButton
                            onClick={() => handleDeleteCard(paymentMethod.id)}
                          />
                        </div>
                        <div className={styles.gpCardContainerSubCell}>
                          <div className={styles.gpCardCardType}>
                            {paymentMethod.card_type}
                          </div>
                          <div className={styles.gpCardLastFour}>
                            **** {paymentMethod.last_four}
                          </div>

                          <div className={styles.gpCardExpiration}>
                            Exp{' '}
                            {paymentMethod.expiration.replace(
                              /(\d{2}\/)\d{2}(\d{2})/,
                              '$1$2'
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              )}
              <div>
                <Button
                  className={styles.gpButton}
                  variant='primary'
                  onClick={handleAddCardByCNP}
                  disabled={isLoading}
                >
                  Add Card Online
                </Button>
                <Button
                  className={styles.gpButton}
                  variant='primary'
                  onClick={() => setModalStep(SELECT_TERMINAL_STEP)}
                  disabled={isLoading}
                >
                  Add Card Via Terminal
                </Button>
              </div>
            </div>
          )}

          {!isLoading && modalStep === SELECT_TERMINAL_STEP && (
            <div className={styles.gpTerminalList}>
              {globalPayDeviceIds &&
                globalPayDeviceIds.map((val, i) => (
                  <CardRadioButton
                    key={i}
                    onClick={() => {
                      setSelectedDeviceId(val[0]);
                    }}
                    checked={val[0] === selectedDeviceId}
                  >
                    {val[1]} - {val[0]}
                  </CardRadioButton>
                ))}
            </div>
          )}

          {!isLoading && modalStep === WAIT_TERMINAL_STEP && (
            <div className={styles.globalPayHostedForm}>
              <div className={styles.loadingSpinner}>
                <Spinner animation='border' variant='secondary' />
              </div>
            </div>
          )}

          {!isLoading && modalStep === ADD_CARD_STEP && (
            <div className={styles.gpPayFieldsForm}>
              {isGlobalPayLoading && (
                <div className={styles.loadingSpinner}>
                  <Spinner animation='border' variant='secondary' />
                </div>
              )}

              <GlobalPayPayFieldsForm
                handleCancel={handleBackwardClick}
                handlePayFieldInformationAccepted={
                  handlePayFieldInformationAccepted
                }
                formLoading={isGlobalPayLoading}
                setFormLoading={setIsGlobalPayLoading}
                displayAddress={false}
              />
            </div>
          )}
        </ModalBody>
        {modalStep !== CARD_INFO_STEP &&
          modalStep !== ADD_CARD_STEP &&
          modalStep !== SELECT_TERMINAL_STEP &&
          modalStep !== WAIT_TERMINAL_STEP &&
          !isGlobalPayLoading && (
            <ModalFooter>
              <Button
                className={styles.button}
                variant='light'
                onClick={handleBackwardClick}
                disabled={isLoading}
              >
                {modalStep === CUSTOMER_INFO_STEP ||
                currentEditMode === editModes.CARD
                  ? 'Cancel'
                  : 'Back'}
              </Button>
              <Button
                id='submit-button'
                className={styles.button}
                variant='primary'
                type='submit'
                disabled={isLoading}
              >
                {modalStep === CARD_INFO_STEP || customerToEdit
                  ? 'Save'
                  : 'Next'}
              </Button>
            </ModalFooter>
          )}
        {modalStep === CARD_INFO_STEP && (
          <ModalFooter>
            <Button
              className={styles.button}
              variant='light'
              onClick={handleBackwardClick}
              disabled={isLoading}
            >
              {currentEditMode === editModes.CARD ? 'Cancel' : 'Back'}
            </Button>
            {currentEditMode === editModes.CARD && (
              <Button
                id='submit-button'
                className={styles.button}
                variant='primary'
                type='submit'
                disabled={isLoading}
                onClick={() => onHide()}
              >
                Close
              </Button>
            )}
          </ModalFooter>
        )}
        {modalStep === SELECT_TERMINAL_STEP && (
          <ModalFooter>
            <Button
              className={styles.button}
              variant='light'
              onClick={handleBackwardClick}
              disabled={isLoading}
            >
              Back
            </Button>
            <Button
              id='submit-button'
              className={styles.button}
              variant='primary'
              type='submit'
              disabled={isLoading}
              onClick={handleAddCardByTerminal}
            >
              Next
            </Button>
          </ModalFooter>
        )}
        {modalStep === WAIT_TERMINAL_STEP && (
          <ModalFooter>
            <Button
              className={styles.button}
              variant='light'
              onClick={handleCancelTerminalAuth}
            >
              Cancel
            </Button>
          </ModalFooter>
        )}
      </form>
    </Modal>
  );
}

export function GlobalPayNewCustomerModal(props) {
  const { show, onHide, onAdd, practiceId } = props;
  return (
    <GlobalPayCustomerModal
      show={show}
      onHide={onHide}
      onAdd={onAdd}
      editMode={editModes.NEW}
      practiceId={practiceId}
    />
  );
}

export function GlobalPayEditCustomerModal(props) {
  const { show, onHide, onUpdate, customerToEdit, practiceId } = props;
  return (
    <GlobalPayCustomerModal
      show={show}
      onHide={onHide}
      customerToEdit={customerToEdit}
      onUpdate={onUpdate}
      editMode={editModes.INFO}
      practiceId={practiceId}
    />
  );
}

export function GlobalPayEditCardModal(props) {
  const { show, onHide, onUpdate, customerToEdit, practiceId } = props;
  return (
    <GlobalPayCustomerModal
      show={show}
      onHide={onHide}
      customerToEdit={customerToEdit}
      onUpdate={onUpdate}
      editMode={editModes.CARD}
      practiceId={practiceId}
    />
  );
}

export default GlobalPayCustomerModal;
